import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { environment } from '@env/environment'
import { ToastrService } from 'ngx-toastr'
import { IHousingUnit } from 'app/models/housingunits/housing-unit'
import { HousingUnitAssociations } from 'app/models/housingunits/housing-unit-associations'
import { HouseholdSearch } from 'app/models/household/household-search'
import { IHousingUnitSearch } from 'app/models/housingunits/housing-unit-search'
import { IWaterSystem } from 'app/models/water-systems/water-system'
import { ITreatmentViewModel, ITreatmentContactViewModel, ITreatmentData, ITreatmentNoteViewModel, ITreatmentFilterChangeViewModel } from 'app/models/household/treatment'

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

const httpOptionsForFiles = {
  withCredentials: true
};

@Injectable({
  providedIn: 'root'
})

export class HousingUnitService {

  existingTreatmentSystemList: string[] = ["Yes", "No", "Unknown"];
  systemInstallPriorityList: string[] = ["High", "Low"];
  installedByList: string[] = ["Hellman Plumbing", "Pure N Soft"];
  systemInstalledList: string[] = ["Culligan", "Kinetico"];
  statusOfInstallList: string[] = ["Installed", "Declined RO System", "Unable to Install"];
  reasonForRefusalList: string[] = ["Unable to Install/Incompatible with Plumbing", "Not Interested", "Has an RO system", "Bottled Water Sufficient"];

  constructor(private toastr: ToastrService, private http?: HttpClient) { }

  get systemInstallPriorityOptions(): string[] {
    return this.systemInstallPriorityList;
  }

  get installedByOptions(): string[] {
    return this.installedByList;
  }

  get systemInstalledOptions(): string[] {
    return this.systemInstalledList;
  }

  get statusOfInstallOptions(): string[] {
    return this.statusOfInstallList;
  }

  get existingTreatmentSystemOptions(): string[] {
    return this.existingTreatmentSystemList;
  }

  get reasonForRefusalOptions(): string[] {
    return this.reasonForRefusalList;
  }

  search(search: IHousingUnitSearch): Observable<IHousingUnit[]> {
    return this.http.post<IHousingUnit[]>(environment.privateApi + 'HousingUnit/Search', search, httpOptions);
  }

  getTreatmentData(housingUnitId: number): Observable<ITreatmentData> {
    return this.http.get<ITreatmentData>(environment.privateApi + 'HousingUnit/TreatmentData/' + housingUnitId, { withCredentials: true });
  }

  getTreatments(housingUnitId: number): Observable<ITreatmentViewModel[]> {
    return this.http.get<ITreatmentViewModel[]>(environment.privateApi + 'HousingUnit/Treatments/' + housingUnitId, { withCredentials: true });
  }

  getTreatment(id: number): Observable<ITreatmentViewModel> {
    return this.http.get<ITreatmentViewModel>(environment.privateApi + 'HousingUnit/Treatment/' + id, { withCredentials: true });
  }

  deleteTreatment(id: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'HousingUnit/Treatment/Delete/' + id, { withCredentials: true });
  }

  saveTreatment(treatment: ITreatmentViewModel): Observable<number> {
    return this.http.post<number>(environment.privateApi + 'HousingUnit/Treatment/Save', treatment, httpOptions);
  }

  getTreatmentContacts(treatmentId: number): Observable<ITreatmentContactViewModel[]> {
    return this.http.get<ITreatmentContactViewModel[]>(environment.privateApi + 'HousingUnit/TreatmentContacts/' + treatmentId, { withCredentials: true });
  }

  saveTreatmentContacts(contacts: ITreatmentContactViewModel[] = []): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/TreatmentContacts/Save', contacts, httpOptions);
  }

  getTreatmentNotes(treatmentId: number): Observable<ITreatmentNoteViewModel[]> {
    return this.http.get<ITreatmentNoteViewModel[]>(environment.privateApi + 'HousingUnit/TreatmentNotes/' + treatmentId, { withCredentials: true });
  }

  saveTreatmentNotes(notes: ITreatmentNoteViewModel[] = []): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/TreatmentNotes/Save', notes, httpOptions);
  }

  getTreatmentFilterChanges(treatmentId: number): Observable<ITreatmentFilterChangeViewModel[]> {
    return this.http.get<ITreatmentFilterChangeViewModel[]>(environment.privateApi + 'HousingUnit/TreatmentFilterChanges/' + treatmentId, { withCredentials: true });
  }

  saveTreatmentFilterChanges(filterChanges: ITreatmentFilterChangeViewModel[] = []): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/TreatmentFilterChanges/Save', filterChanges, httpOptions);
  }

  getHousingUnitById(housingUnitId: number): Observable<IHousingUnit> {
    return this.http.get<IHousingUnit>(environment.privateApi + 'HousingUnit/' + housingUnitId, { withCredentials: true });
  }

  getAssociatedHousingUnitById(housingUnitId: number): Observable<any> {
    return this.http.get<IHousingUnit>(environment.privateApi + 'HousingUnit/' + housingUnitId, { withCredentials: true });
  }

  downloadCsv(search: HouseholdSearch): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/DownloadCsv', search, httpOptions);
  }

  saveHousingUnit(housingUnit: IHousingUnit): Observable<number> {
    return this.http.post<number>(environment.privateApi + 'HousingUnit/Save', housingUnit, httpOptions);
  }

  mergeHousingUnit(id: number, ids: number[]): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/MergeHousingUnits/' + id, ids, httpOptions);
  }

  getCounties(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/county', { withCredentials: true });
  }

  getHousingUnitAssociations(housingUnitId: number): Observable<HousingUnitAssociations> {
    return this.http.get<HousingUnitAssociations>(environment.privateApi + 'HousingUnit/Associations/' + housingUnitId, { withCredentials: true });
  }

  getHousingUnitAssociationById(id: number, housingUnitKey: number): Observable<HousingUnitAssociations> {
    return this.http.get<HousingUnitAssociations>(environment.privateApi + 'HousingUnit/AssociationById/' + id + '/' + housingUnitKey, { withCredentials: true });
  }

  saveHousingUnitAssociation(housingUnitAssocations: HousingUnitAssociations): Observable<any> {
    return this.http.post<HousingUnitAssociations>(environment.privateApi + 'HousingUnit/SaveHousingUnitAssociation/', housingUnitAssocations, { withCredentials: true });
  }

  getWellHousingUnits(wellId: number): Observable<IHousingUnit[]> {
    return this.http.get<IHousingUnit[]>(environment.privateApi + 'HousingUnit/WellHousingUnits/' + wellId, { withCredentials: true });
  }

  disassociateFromHousingUnit(householdHousingUnitKey: number): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/DisassociateHousingUnit/' + householdHousingUnitKey, httpOptions);
  }

  disassociateHousingUnitFromWell(wellHousingUnitKey: number): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/DisassociateWell/' + wellHousingUnitKey, httpOptions);
  }

  associateHousingUnitToWell(housingUnitAssociations: HousingUnitAssociations): Observable<any> {
    return this.http.post(environment.privateApi + 'HousingUnit/AssociateHousingUnitToWell/', housingUnitAssociations, httpOptions);
  }

  deleteHousingUnit(id: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'HousingUnit/Delete/' + id, { withCredentials: true });
  }

  deleteHousingUnitWaterSystem(id: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'HousingUnit/WaterSystem/Delete/' + id, { withCredentials: true });
  }

  getHousingUnitWaterSystems(housingUnitId: number): Observable<IWaterSystem[]> {
    return this.http.get<IWaterSystem[]>(environment.privateApi + 'HousingUnit/WaterSystems/' + housingUnitId, { withCredentials: true });
  }

  getHousingUnitWaterSystemById(id: number): Observable<IWaterSystem> {
    return this.http.get<IWaterSystem>(environment.privateApi + 'HousingUnit/WaterSystem/' + id, { withCredentials: true });
  }

  saveHousingUnitWaterSystem(waterSystem: IWaterSystem): Observable<any> {
    return this.http.post<IWaterSystem>(environment.privateApi + 'HousingUnit/SaveHousingUnitWaterSystem/', waterSystem, { withCredentials: true });
  }

  unlinkTreatmentSample(treatmentId: number, sampleId: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'HousingUnit/UnlinkTreatmentSample/' + treatmentId + "/" + sampleId, { withCredentials: true });
  }

  linkTreatmentToSample(treatmentId: number, sampleId: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'HousingUnit/LinkTreatmentSample/' + treatmentId + "/" + sampleId, { withCredentials: true });
  }
}